/* Granville pickup 10 km location check.
 *
 * Shared by:
 *   - snippets/granville-location-popup.liquid  (invite popup on other collection pages)
 *   - sections/granville-gate.liquid             (locks the Granville collection page until checked)
 *
 * Flow (guide section 5):
 *   "Check my location" -> browser geolocation -> straight-line distance to the pickup point
 *     within radius            -> verified (saved for 30 days)
 *     outside radius           -> not verified
 *     declined / unavailable /
 *     too rough near boundary  -> "Enter an address" (Geoapify geocoding below)
 *
 * This is a browser-side, honour-system check: it decides what the visitor is shown and whether the
 * theme applies the Granville discount codes. It is NOT security. The Shopify Flow pickup check and
 * collection in person are the real safeguards (see docs/granville-pickup-option-a-plan.pdf).
 * ?granville_test=1 counts as verified and saves nothing, for previewing.
 */
(function () {
  if (window.GranvilleLocation) return;

  var VERIFY_KEY = 'granvilleVerified';
  var CHOICE_KEY = 'granvillePickupChoice';
  var VERIFY_MS = 30 * 24 * 60 * 60 * 1000;
  var GEOAPIFY_URL = 'https://api.geoapify.com/v1/geocode/search';

  var configEl = document.querySelector('[data-granville-location-config]');
  var config = {};
  try {
    config = JSON.parse(configEl ? configEl.textContent : '{}');
  } catch (e) {
    config = {};
  }
  var pickup = { lat: parseFloat(config.lat), lng: parseFloat(config.lng) };
  var radiusKm = parseFloat(config.radius) || 10;
  var geoapifyKey = (config.geoapifyKey || '').trim();
  var testMode = new URLSearchParams(window.location.search).get('granville_test') === '1';

  /* ---------- Storage ---------- */

  function read(key) {
    try {
      return JSON.parse(window.localStorage.getItem(key));
    } catch (e) {
      return null;
    }
  }

  function write(key, value) {
    if (testMode) return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* Storage blocked (private mode): the visitor will be asked again next visit. */
    }
  }

  function isVerified() {
    if (testMode) return true;
    var saved = read(VERIFY_KEY);
    return !!(saved && saved.within && Date.now() - saved.at < VERIFY_MS);
  }

  function saveResult(within) {
    write(VERIFY_KEY, { within: within, at: Date.now() });
  }

  /* ---------- Distance ---------- */

  function hasPickupPoint() {
    return isFinite(pickup.lat) && isFinite(pickup.lng);
  }

  // Haversine great-circle (straight-line) distance in km.
  function distanceKm(lat, lng) {
    var toRad = Math.PI / 180;
    var dLat = (lat - pickup.lat) * toRad;
    var dLng = (lng - pickup.lng) * toRad;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(pickup.lat * toRad) * Math.cos(lat * toRad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  // Resolves to 'within', 'outside' or 'address' (too rough near the boundary).
  function evaluate(lat, lng, accuracyMetres) {
    var km = distanceKm(lat, lng);
    var accuracyKm = (accuracyMetres || 0) / 1000;
    if (accuracyKm && Math.abs(km - radiusKm) < accuracyKm) return 'address';
    var within = km <= radiusKm;
    saveResult(within);
    return within ? 'within' : 'outside';
  }

  /* ------------------------------------------------------------------------------------------
   * Address geocoding: Geoapify Geocoding API (https://www.geoapify.com/geocoding-api/).
   * Needs the API key from Theme settings > Granville pickup. The key is visible in the browser,
   * so restrict it to the store's domain in the Geoapify dashboard.
   * The free plan requires attribution, shown under each address field.
   * Resolves to { lat: Number, lng: Number }, or null when the address can't be located.
   * ------------------------------------------------------------------------------------------ */
  function geocodeAddress(address) {
    if (!geoapifyKey) {
      if (window.console) console.warn('[Granville] Geoapify API key is not set in theme settings.');
      return Promise.resolve(null);
    }
    var params = new URLSearchParams({
      text: address.slice(0, 200),
      filter: 'countrycode:au',
      lang: 'en',
      limit: '1',
      format: 'json',
      apiKey: geoapifyKey
    });
    var controller = window.AbortController ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, 8000) : null;
    return fetch(GEOAPIFY_URL + '?' + params.toString(), {
      headers: { Accept: 'application/json' },
      signal: controller ? controller.signal : undefined
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Geoapify ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var hit = data && data.results && data.results[0];
        var lat = hit ? parseFloat(hit.lat) : NaN;
        var lng = hit ? parseFloat(hit.lon) : NaN;
        return isFinite(lat) && isFinite(lng) ? { lat: lat, lng: lng } : null;
      })
      .catch(function (error) {
        if (window.console) console.warn('[Granville] Address lookup failed:', error.message);
        return null;
      })
      .finally(function () {
        if (timer) clearTimeout(timer);
      });
  }

  /* ---------- Checks: resolve to 'within' | 'outside' | 'address' | 'notfound' | 'unconfigured' ---------- */

  function checkLocation() {
    if (!hasPickupPoint()) {
      if (window.console) console.warn('[Granville] Pickup latitude/longitude are not set in theme settings.');
      return Promise.resolve('unconfigured');
    }
    if (!navigator.geolocation) return Promise.resolve('address');
    return new Promise(function (resolve) {
      navigator.geolocation.getCurrentPosition(
        function (pos) { resolve(evaluate(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy)); },
        function () { resolve('address'); },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
      );
    });
  }

  function checkAddress(address) {
    if (!hasPickupPoint()) return Promise.resolve('unconfigured');
    return geocodeAddress(address).then(function (result) {
      return result ? evaluate(result.lat, result.lng, 0) : 'notfound';
    });
  }

  /* ---------- Shared UI helpers ---------- */

  function busy(button, on) {
    if (!button) return;
    button.disabled = on;
    if (on) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }

  function bindAddressForm(form, onResult) {
    form.addEventListener('submit', function (evt) {
      evt.preventDefault();
      var input = form.querySelector('input');
      var submit = form.querySelector('[type="submit"]');
      var address = input.value.trim();
      if (!address) {
        input.focus();
        return;
      }
      if (submit.disabled) return;
      busy(submit, true);
      checkAddress(address).then(function (result) {
        busy(submit, false);
        onResult(result, input);
      });
    });
  }

  /* ---------- Invite popup (other collection pages) ---------- */

  function initPopup(popup) {
    var dialog = popup.querySelector('[role="dialog"]');
    var lastFocus = null;

    function showStep(name) {
      popup.querySelectorAll('[data-granville-step]').forEach(function (step) {
        step.hidden = step.dataset.granvilleStep !== name;
      });
      var active = popup.querySelector('[data-granville-step="' + name + '"]');
      var title = active && active.querySelector('h2');
      if (title) dialog.setAttribute('aria-labelledby', title.id || (title.id = 'GranvillePopupTitle-' + name));
      var focusTarget = active && active.querySelector('input, a[href], button');
      if (focusTarget) focusTarget.focus();
    }

    function onKeydown(evt) {
      if (evt.key === 'Escape') {
        close('dismissed');
        return;
      }
      if (evt.key !== 'Tab') return;
      // Keep focus inside the dialog while it is open.
      var focusable = Array.prototype.filter.call(
        dialog.querySelectorAll('a[href], button, input'),
        function (el) { return !el.closest('[hidden]') && !el.disabled; }
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault();
        first.focus();
      }
    }

    function open(step) {
      lastFocus = document.activeElement;
      popup.hidden = false;
      document.addEventListener('keydown', onKeydown);
      showStep(step);
    }

    function close(choice) {
      if (choice) write(CHOICE_KEY, { choice: choice, at: Date.now() });
      popup.hidden = true;
      document.removeEventListener('keydown', onKeydown);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    function handle(result) {
      if (result === 'within') showStep('within');
      else if (result === 'address') showStep('address');
      else if (result === 'outside') close('outside');
      else if (result === 'notfound') close('address-unverified');
      else close(); // unconfigured: ask again next visit
    }

    var locateBtn = popup.querySelector('[data-granville-locate]');
    locateBtn.addEventListener('click', function () {
      busy(locateBtn, true);
      checkLocation().then(function (result) {
        busy(locateBtn, false);
        handle(result);
      });
    });

    bindAddressForm(popup.querySelector('[data-granville-step="address"]'), handle);

    popup.querySelectorAll('[data-granville-continue], [data-granville-close]').forEach(function (btn) {
      btn.addEventListener('click', function () { close('dismissed'); });
    });
    popup.querySelector('[data-granville-show]').addEventListener('click', function () {
      write(CHOICE_KEY, { choice: 'show-offers', at: Date.now() });
    });

    if (testMode) {
      open('within');
    } else if (!read(CHOICE_KEY) && !isVerified()) {
      setTimeout(function () { open('prompt'); }, 1500);
    }
  }

  /* ---------- Page gate (Granville collection) ---------- */

  function initGate(gate) {
    var root = document.documentElement;
    var message = gate.querySelector('[data-granville-gate-message]');

    function unlock() {
      root.classList.remove('gp-locked');
      root.classList.add('gp-unlocked');
      window.scrollTo(0, 0);
      var heading = document.querySelector('.granville-section:not(.granville-gate-section) h2');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
    }

    function showStep(name) {
      gate.querySelectorAll('[data-granville-step]').forEach(function (step) {
        step.hidden = step.dataset.granvilleStep !== name;
      });
    }

    function say(key) {
      message.textContent = key ? (gate.dataset[key] || '') : '';
      message.hidden = !key;
    }

    function handle(result, input) {
      if (result === 'within') {
        unlock();
      } else if (result === 'address') {
        say('');
        showStep('address');
        gate.querySelector('[data-granville-step="address"] input').focus();
      } else if (result === 'outside') {
        say('outsideMessage');
      } else if (result === 'notfound') {
        say('notFoundMessage');
        if (input) input.focus();
      } else {
        say('unavailableMessage');
      }
    }

    var locateBtn = gate.querySelector('[data-granville-locate]');
    locateBtn.addEventListener('click', function () {
      say('');
      busy(locateBtn, true);
      checkLocation().then(function (result) {
        busy(locateBtn, false);
        handle(result);
      });
    });

    var showAddress = gate.querySelector('[data-granville-show-address]');
    if (showAddress) {
      showAddress.addEventListener('click', function () {
        say('');
        showStep('address');
        gate.querySelector('[data-granville-step="address"] input').focus();
      });
    }

    bindAddressForm(gate.querySelector('[data-granville-step="address"]'), handle);

    if (isVerified()) unlock();
  }

  window.GranvilleLocation = { isVerified: isVerified };

  function init() {
    var popup = document.querySelector('[data-granville-popup]');
    if (popup) initPopup(popup);
    var gate = document.querySelector('[data-granville-gate]');
    if (gate) initGate(gate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
